import {
  createContext,
  type ReactNode,
  type Reducer,
  useContext,
  useEffect,
  useReducer,
} from "react";

import { Field } from "@/utils/form";

const STORE_NAME = "store";

interface Store {
  ready: Readonly<boolean>;
  email: Readonly<string>;
  fields: Field[] | null;
  instanceKey: string;
  currentQuestion: string;
  activation_key: string;
  userFirstName: string | null;
  userLastName: string | null;
  userEmail: string | null;
  userMarketingOptIn: boolean | null;
  activationFormValues: Record<string, string | boolean> | null;
}

const getDefaultStore = (): Store => ({
  ready: false,
  email: "",
  fields: null,
  instanceKey: "",
  currentQuestion: "QUESTION_1",
  activation_key: "",
  userFirstName: null,
  userLastName: null,
  userEmail: null,
  userMarketingOptIn: null,
  activationFormValues: null,
});

const getStoredState = () => {
  if (typeof localStorage === "undefined") return getDefaultStore();
  const storedState = localStorage.getItem(STORE_NAME);
  return storedState ? JSON.parse(storedState) : getDefaultStore();
};

const RESET_ANSWERS = "RESET_ANSWERS";
const RESET_STORE = "RESET_STORE";
const SET_READY = "SET_READY";
const SET_FIELD_ANSWER = "SET_FIELD_ANSWER";
const SET_EMAIL = "SET_EMAIL";
const SET_FIELDS = "SET_FIELDS";
const SET_INSTANCE_KEY = "SET_INSTANCE_KEY";
const SET_CURRENT_QUESTION = "SET_CURRENT_QUESTION";
const SET_STORE = "SET_STORE";
const SET_ACTIVATION_KEY = "SET_ACTIVATION_KEY";
const SET_USER_FIRST_NAME = "SET_USER_FIRST_NAME";
const SET_USER_LAST_NAME = "SET_USER_LAST_NAME";
const SET_USER_EMAIL = "SET_USER_EMAIL";
const SET_USER_MARKETING_OPT_IN = "SET_USER_MARKETING_OPT_IN";
const SET_ACTIVATION_FORM_VALUES = "SET_ACTIVATION_FORM_VALUES";

type Action = {
  type:
    | typeof RESET_ANSWERS
    | typeof RESET_STORE
    | typeof SET_READY
    | typeof SET_FIELD_ANSWER
    | typeof SET_EMAIL
    | typeof SET_FIELDS
    | typeof SET_INSTANCE_KEY
    | typeof SET_CURRENT_QUESTION
    | typeof SET_STORE
    | typeof SET_ACTIVATION_KEY
    | typeof SET_USER_FIRST_NAME
    | typeof SET_USER_LAST_NAME
    | typeof SET_USER_EMAIL
    | typeof SET_USER_MARKETING_OPT_IN
    | typeof SET_ACTIVATION_FORM_VALUES;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  payload?: any;
};

const storeReducer: Reducer<Store, Action> = (state, action) => {
  switch (action.type) {
    case SET_READY:
      return { ...state, ready: action.payload };
    case RESET_ANSWERS:
      return {
        ...state,
        fields: state.fields?.map((field) => ({ ...field, answer: null })),
        currentQuestion: "QUESTION_1",
      };
    case RESET_STORE:
      return getDefaultStore();
    case SET_FIELD_ANSWER:
      return {
        ...state,
        fields: state.fields?.map((field) =>
          field.key === action.payload.key
            ? { ...field, answer: action.payload.answer }
            : field,
        ),
      };
    case SET_FIELDS:
      return { ...state, fields: action.payload };
    case SET_EMAIL:
      return { ...state, email: action.payload };
    case SET_INSTANCE_KEY:
      return { ...state, instanceKey: action.payload };
    case SET_CURRENT_QUESTION:
      return { ...state, currentQuestion: action.payload };
    case SET_ACTIVATION_KEY:
      return { ...state, activation_key: action.payload };
    case SET_USER_FIRST_NAME:
      return { ...state, userFirstName: action.payload };
    case SET_USER_LAST_NAME:
      return { ...state, userLastName: action.payload };
    case SET_USER_EMAIL:
      return { ...state, userEmail: action.payload };
    case SET_USER_MARKETING_OPT_IN:
      return { ...state, userMarketingOptIn: action.payload };
    case SET_ACTIVATION_FORM_VALUES:
      return { ...state, activationFormValues: action.payload };
    case SET_STORE:
      return action.payload;
    default:
      return state;
  }
};

type StoreType = Store & {
  resetAnswers: () => void;
  resetStore: () => void;
  setFieldAnswer: (_key: string, _answer: string) => void;
  setEmail: (_email: string) => void;
  setFields: (_fields: Field[]) => void;
  setInstanceKey: (_instanceKey: string) => void;
  setCurrentQuestion: (_currentQuestion: string) => void;
  setActivationKey: (_activationKey: string | null) => void;
  setUserFirstName: (_firstName: string | null) => void;
  setUserLastName: (_lastName: string | null) => void;
  setUserEmail: (_email: string | null) => void;
  setUserMarketingOptIn: (_optIn: boolean | null) => void;
  setActivationFormValues: (
    _values: Record<string, string | boolean> | null,
  ) => void;
};

const StoreContext = createContext<StoreType>({
  ...getDefaultStore(),
  resetAnswers: () => {},
  resetStore: () => {},
  setFieldAnswer: (_key: string, _answer: string) => {},
  setEmail: (_email: string) => {},
  setFields: (_fields: Field[]) => {},
  setInstanceKey: (_instanceKey: string) => {},
  setCurrentQuestion: (_currentQuestion: string) => {},
  setActivationKey: (_activationKey: string | null) => {},
  setUserFirstName: (_firstName: string | null) => {},
  setUserLastName: (_lastName: string | null) => {},
  setUserEmail: (_email: string | null) => {},
  setUserMarketingOptIn: (_optIn: boolean | null) => {},
  setActivationFormValues: (
    _values: Record<string, string | boolean> | null,
  ) => {},
});

export const useStore = () => {
  return useContext(StoreContext);
};

type Props = {
  children: ReactNode;
};

export const StoreProvider = ({ children }: Props) => {
  const [state, dispatch] = useReducer(storeReducer, getStoredState());

  useEffect(() => {
    localStorage.setItem(STORE_NAME, JSON.stringify(state));
  }, [state]);

  // Define actions
  const resetAnswers = () => dispatch({ type: RESET_ANSWERS });
  const resetStore = () => dispatch({ type: RESET_STORE });
  const setFieldAnswer = (key: string, answer: string) =>
    dispatch({ type: SET_FIELD_ANSWER, payload: { key, answer } });
  const setEmail = (email: string) =>
    dispatch({ type: SET_EMAIL, payload: email });
  const setFields = (fields: Field[]) =>
    dispatch({ type: SET_FIELDS, payload: fields });
  const setInstanceKey = (instanceKey: string) =>
    dispatch({ type: SET_INSTANCE_KEY, payload: instanceKey });
  const setCurrentQuestion = (currentQuestion: string) =>
    dispatch({ type: SET_CURRENT_QUESTION, payload: currentQuestion });
  const setActivationKey = (activationKey: string | null) =>
    dispatch({ type: SET_ACTIVATION_KEY, payload: activationKey });
  const setUserFirstName = (firstName: string | null) =>
    dispatch({ type: SET_USER_FIRST_NAME, payload: firstName });
  const setUserLastName = (lastName: string | null) =>
    dispatch({ type: SET_USER_LAST_NAME, payload: lastName });
  const setUserEmail = (userEmail: string | null) =>
    dispatch({ type: SET_USER_EMAIL, payload: userEmail });
  const setUserMarketingOptIn = (optIn: boolean | null) =>
    dispatch({ type: SET_USER_MARKETING_OPT_IN, payload: optIn });
  const setActivationFormValues = (
    values: Record<string, string | boolean> | null,
  ) => dispatch({ type: SET_ACTIVATION_FORM_VALUES, payload: values });

  // Define additional methods
  const $reset = () => dispatch({ type: RESET_STORE });
  const isQuizComplete = () => {
    return state.fields?.every((field) => field.answer !== null) || false;
  };

  // Context value
  const contextValue = {
    ...state,
    resetAnswers,
    resetStore,
    $reset,
    isQuizComplete,
    setFieldAnswer,
    setEmail,
    setFields,
    setInstanceKey,
    setCurrentQuestion,
    setActivationKey,
    setUserFirstName,
    setUserLastName,
    setUserEmail,
    setUserMarketingOptIn,
    setActivationFormValues,
  };

  return (
    <StoreContext.Provider value={contextValue}>
      {children}
    </StoreContext.Provider>
  );
};
